'use client'
import {
  useSubscription
} from "./chunk-NAUELPNF.mjs";
import {
  useUserSubscription
} from "./chunk-DZQX2LRT.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/hooks/useSubscriptionTicketBenefits.ts
import { useMemo } from "react";
import { useGetSubscriptionActions, useGetSubscriptionPlans } from "@wbk/api/hooks";
import { useRouter } from "@wbk/config";
var useSubscriptionTicketBenefits = ({
  action = "booking_limit",
  selectedSlot,
  event,
  selectedTickets
}) => {
  var _a;
  const { slug, active } = useSubscription();
  const { locale } = useRouter();
  const { subscriptions } = useUserSubscription();
  const activeAction = active && !!((_a = event.subscription_benefits) == null ? void 0 : _a[action]);
  const { data, isLoading } = useGetSubscriptionActions(
    {
      actions: [action],
      slug: event.slug,
      lang: locale,
      selected_timeslot: selectedSlot
    },
    {
      enabled: activeAction,
      staleTime: Infinity,
      gcTime: Infinity
    }
  );
  const { data: plans } = useGetSubscriptionPlans(
    { subscriptionSlug: slug, lang: locale },
    { enabled: active }
  );
  const subscriptionTicketTypeIds = useMemo(() => {
    var _a2;
    return Object.keys(((_a2 = event.subscription_benefits) == null ? void 0 : _a2[action]) || {});
  }, [action, event.subscription_benefits]);
  const isActiveAction = useMemo(() => {
    var _a2;
    return !!((_a2 = event.subscription_benefits) == null ? void 0 : _a2[action]);
  }, [action, event.subscription_benefits]);
  const benefitValue = useMemo(() => {
    var _a2, _b, _c;
    const value = {};
    if (!isActiveAction) {
      return value;
    }
    for (const subscriptionTicketTypeId of subscriptionTicketTypeIds) {
      const benefitsFromAction = (_a2 = data == null ? void 0 : data[action]) == null ? void 0 : _a2[subscriptionTicketTypeId];
      if (benefitsFromAction) {
        value[subscriptionTicketTypeId] = benefitsFromAction.value - benefitsFromAction.consumed_value;
      } else {
        const possibleBenefits = ((_c = (_b = event.subscription_benefits) == null ? void 0 : _b[action]) == null ? void 0 : _c[subscriptionTicketTypeId]) || {};
        if (possibleBenefits) {
          let max;
          const benefits = Object.values(possibleBenefits) || [];
          for (const sub of subscriptions) {
            const benefit = benefits.find((ben) => sub.plan._id === ben.subscription_plan_id);
            if (benefit) {
              const val = benefit.value - benefit.consumed_value;
              const validMax = max || 0;
              if (val > validMax) {
                max = val;
              }
            }
          }
          value[subscriptionTicketTypeId] = max;
        }
      }
    }
    return value;
  }, [
    isActiveAction,
    subscriptionTicketTypeIds,
    data,
    action,
    event.subscription_benefits,
    subscriptions
  ]);
  const upgradablePlans = useMemo(() => {
    var _a2, _b, _c;
    const plans2 = {};
    if (!isActiveAction) {
      return plans2;
    }
    for (const subscriptionTicketTypeId of subscriptionTicketTypeIds) {
      const hasAction = (_a2 = data == null ? void 0 : data[action]) == null ? void 0 : _a2[subscriptionTicketTypeId];
      if (hasAction) {
        const upgradables = Object.values((hasAction == null ? void 0 : hasAction.upgradable_plans) || {});
        plans2[subscriptionTicketTypeId] = upgradables.map((up) => __spreadProps(__spreadValues({}, up), {
          value: Math.max(up.value - hasAction.consumed_value, 0)
        }));
      } else {
        const possibleBenefits = (_c = (_b = event.subscription_benefits) == null ? void 0 : _b[action]) == null ? void 0 : _c[subscriptionTicketTypeId];
        const upgradablePlans2 = Object.values(possibleBenefits || {});
        plans2[subscriptionTicketTypeId] = upgradablePlans2;
      }
    }
    return plans2;
  }, [action, data, event.subscription_benefits, isActiveAction, subscriptionTicketTypeIds]);
  const userCurrentBenefits = useMemo(() => {
    var _a2, _b;
    const benefits = [];
    if (!isActiveAction) {
      return benefits;
    }
    if (action === "booking_limit") {
      const defaultBenefits = Object.keys((data == null ? void 0 : data.event_booking_limit) || {});
      for (const key of defaultBenefits) {
        const value = data == null ? void 0 : data.event_booking_limit[key];
        if (value) {
          const text = `${value} ${key}`;
          if (!benefits.includes(text)) {
            benefits.push(text);
          }
        }
      }
    }
    const fromAction = Object.values((data == null ? void 0 : data[action]) || {});
    if (fromAction.length > 0) {
      for (const benefit of fromAction) {
        const text = `${benefit.value - benefit.consumed_value} ${benefit.benefit_name}`;
        if (!benefits.includes(text)) {
          benefits.push(text);
        }
      }
      return benefits;
    }
    for (const subscriptionTicketTypeId of subscriptionTicketTypeIds) {
      const possibleBenefits = (_b = (_a2 = event.subscription_benefits) == null ? void 0 : _a2[action]) == null ? void 0 : _b[subscriptionTicketTypeId];
      if (possibleBenefits) {
        const bens = Object.values(possibleBenefits) || [];
        const actionIds = bens.map((ben) => ben.benefit_action_id);
        for (const sub of subscriptions) {
          const userBenefits = sub.plan.benefits.filter(
            (ben) => actionIds.includes(ben.benefit_action_id || "")
          );
          for (const benefit of userBenefits) {
            const title = `${benefit.benefit_action_value} ${benefit.name}`;
            if (!benefits.includes(title)) {
              benefits.push(title);
            }
          }
        }
      }
    }
    return benefits;
  }, [
    isActiveAction,
    action,
    data,
    subscriptionTicketTypeIds,
    event.subscription_benefits,
    subscriptions
  ]);
  const quantities = useMemo(() => {
    const list = {};
    for (const ticket of selectedTickets) {
      const ticketTypeId = ticket.subscription_ticket_type_id;
      if (ticketTypeId) {
        const prevQty = list[ticketTypeId] || 0;
        list[ticketTypeId] = prevQty + ticket.qty;
      }
    }
    return list;
  }, [selectedTickets]);
  const mustUpgradeToPlans = useMemo(() => {
    var _a2, _b, _c;
    const allPlans = {};
    let highest = [];
    let highestValueOfAllTypes = 0;
    for (const subscriptionTypeId in quantities) {
      const qty = quantities[subscriptionTypeId];
      const limit = benefitValue[subscriptionTypeId];
      const subjectToLimit = typeof limit === "number";
      if (subjectToLimit && qty > limit) {
        highestValueOfAllTypes = Math.max(highestValueOfAllTypes, qty);
        const higherPlans = ((_a2 = upgradablePlans[subscriptionTypeId]) == null ? void 0 : _a2.filter(
          (p) => p.value >= qty && p.value >= highestValueOfAllTypes
        )) || [];
        allPlans[subscriptionTypeId] = upgradablePlans[subscriptionTypeId];
        if (!highest.length || higherPlans.length < highest.length) {
          highest = higherPlans;
        }
      }
    }
    const userPlanId = (_b = subscriptions == null ? void 0 : subscriptions[0]) == null ? void 0 : _b.plan._id;
    const neededPlanIds = event.booking_limit_subscription_plan_ids || [];
    const allSubPlans = ((_c = plans == null ? void 0 : plans.plan_groups) == null ? void 0 : _c.flatMap((p) => p.plans)) || [];
    let mustHavePlan;
    if (active && !!event.subscription_benefits) {
      if (neededPlanIds.length > 0) {
        mustHavePlan = neededPlanIds.includes(userPlanId) ? void 0 : allSubPlans.find((p) => neededPlanIds.includes(p._id));
      } else {
        mustHavePlan = allSubPlans.find((p) => !p.is_basic_plan);
      }
    }
    const shouldHavePlan = !!neededPlanIds.length && !!mustHavePlan;
    const shouldUpgrade = shouldHavePlan || Object.keys(allPlans).length > 0;
    return {
      allPlans,
      highest,
      mustHavePlan,
      shouldUpgrade
    };
  }, [
    active,
    benefitValue,
    event.booking_limit_subscription_plan_ids,
    event.subscription_benefits,
    plans == null ? void 0 : plans.plan_groups,
    quantities,
    subscriptions,
    upgradablePlans
  ]);
  return {
    isActive: activeAction,
    isLoading,
    subscriptions,
    benefitValue,
    upgradablePlans,
    userCurrentBenefits,
    quantities,
    mustUpgradeToPlans
  };
};

export {
  useSubscriptionTicketBenefits
};
//# sourceMappingURL=chunk-DYNZP6IM.mjs.map